import React, { useState, useEffect } from 'react';
import { Card, Alert } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { LanguageSwitcher } from '../../components/shared';
import './PatientRegistration.css';

export function RegistrationComplete() {
  const [searchParams] = useSearchParams();
  
  // Language state - detect from URL or default to Greek for patients
  const [language, setLanguage] = useState<'en' | 'el'>('el');

  // Detect language from URL parameters, sessionStorage, or localStorage on component mount
  useEffect(() => {
    const urlLang = searchParams.get('lang');
    const sessionLang = sessionStorage.getItem('registrationCompletionLanguage');
    const savedLang = localStorage.getItem('patientRegistrationLanguage');
    
    if (urlLang === 'en' || urlLang === 'el') {
      setLanguage(urlLang);
    } else if (sessionLang === 'en' || sessionLang === 'el') {
      setLanguage(sessionLang);
    } else if (savedLang === 'en' || savedLang === 'el') {
      setLanguage(savedLang);
    }
    
    // Clean up sessionStorage after using it
    if (sessionLang) {
      sessionStorage.removeItem('registrationCompletionLanguage');
    }
  }, [searchParams]);

  // Save language preference when it changes
  useEffect(() => {
    localStorage.setItem('patientRegistrationLanguage', language);
  }, [language]);

  // Translation helper
  const t = (key: string): string => {
    const translations: Record<string, { el: string; en: string }> = {
      // Main heading
      registrationComplete: { 
        el: 'Η Εγγραφή σας Ολοκληρώθηκε!', 
        en: 'Patient Registration Complete!' 
      },
      
      // Thank you section
      thankYouTitle: { 
        el: 'Ευχαριστούμε που Συμπληρώσατε το Προφίλ σας', 
        en: 'Thank You for Completing Your Patient Profile' 
      },
      thankYouMessage: { 
        el: 'Οι πλήρεις πληροφορίες σας έχουν υποβληθεί και αποθηκευτεί με ασφάλεια. Ο πάροχος υγειονομικής περίθαλψής σας έχει πλέον πρόσβαση στο πλήρες ιατρικό και προσωπικό σας προφίλ για να σας παρέχει την καλύτερη δυνατή φροντίδα.', 
        en: 'Your comprehensive patient information has been successfully submitted and securely saved. Your healthcare provider now has access to your complete medical and personal profile to provide you with the best possible care.' 
      },
      
      // Confirmation email section
      confirmationEmailTitle: { 
        el: 'Email Επιβεβαίωσης', 
        en: 'Confirmation Email Sent' 
      },
      confirmationEmailMessage: { 
        el: 'Ένα λεπτομερές email επιβεβαίωσης με σύνοψη όλων των πληροφοριών σας έχει σταλεί στη διεύθυνση email σας. Παρακαλώ ελέγξτε τα εισερχόμενά σας για την πλήρη αναφορά.', 
        en: 'A detailed confirmation email with a summary of all your information has been sent to your email address. Please check your inbox for the complete report.' 
      },
      
      // Close window message
      closeWindowMessage: { 
        el: 'Μπορείτε να κλείσετε αυτό το παράθυρο με ασφάλεια τώρα. Σας ευχαριστούμε για τον χρόνο σας!', 
        en: 'You can safely close this window now. Thank you for your time!' 
      }
    };
    
    return translations[key]?.[language] || key;
  };

  return (
    <div className="registration-complete-container">
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            {/* Language switcher using universal component */}
            <div className="d-flex justify-content-end mb-3">
              <LanguageSwitcher
                selectedLanguage={language}
                onLanguageChange={setLanguage}
                variant="compact"
                className="registration-complete-lang-switcher"
              />
            </div>
            
            <Card className="shadow-lg border-0">
              <Card.Body className="text-center p-5">
                <div className="mb-4">
                  <div className="success-icon-wrapper">
                    <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '5rem' }}></i>
                  </div>
                </div>
                
                <h1 className="mb-3 text-success fw-bold">
                  <i className="bi bi-clipboard-heart me-2"></i>
                  {t('registrationComplete')}
                </h1>
                
                <Alert variant="success" className="mb-4 border-0" style={{ backgroundColor: '#d1edff' }}>
                  <h5 className="mb-3 text-primary">
                    <i className="bi bi-check-circle-fill me-2"></i>
                    {t('thankYouTitle')}
                  </h5>
                  <p className="mb-2 text-dark">
                    {t('thankYouMessage')}
                  </p>
                  <div className="mt-3 p-3 bg-white rounded border-start border-4 border-info">
                    <h6 className="text-info mb-2">
                      <i className="bi bi-envelope-check me-2"></i>
                      {t('confirmationEmailTitle')}
                    </h6>
                    <p className="mb-0 text-dark small">
                      {t('confirmationEmailMessage')}
                    </p>
                  </div>
                </Alert>
              
              <div className="text-center">
                <div className="p-3 bg-light rounded">
                  <p className="mb-0 text-muted">
                    <i className="bi bi-info-circle me-2"></i>
                    {t('closeWindowMessage')}
                  </p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
      </div>
    </div>
  );
}